/**
 * Admin Dashboard — system-wide reporting for bots, users, and platform health.
 * Route: /admin (AdminRoute-protected)
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  RefreshCw, Activity, Users, Cpu, Heart, Loader,
  AlertTriangle, Clock, BarChart2, Zap,
} from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import Navigation from '../components/Navigation';
import UserBar from '../components/UserBar';
import { adminDashboardDataEurW } from '../firebaseConfig';
import '../styles/admin.css';

interface DashboardData {
  fleet: {
    total: number;
    active: number;
    paused: number;
    byLevel: Record<string, number>;
  };
  activity: {
    today: { post: number; reply: number; like: number; message: number; total: number };
    yesterday: { post: number; reply: number; like: number; message: number; total: number };
  };
  budgetUtilization: Array<{
    level: string;
    bots: number;
    avgUsed: number;
    budget: number;
    utilPct: number;
    over: number;
    idle: number;
  }>;
  hourlyDistribution: number[];
  engagement: {
    botPosts: number;
    totalLikes: number;
    humanReplies: number;
    botReplies: number;
    topPosts: Array<{
      id: string;
      title: string;
      likes: number;
      humanReplies: number;
      botReplies: number;
      timestamp: string | null;
    }>;
  };
  health: {
    flaggedBots: Array<{
      id: string;
      reports: number;
      blocks: number;
      status: string;
    }>;
  };
  queue: {
    pending: number;
    processing: number;
    failed: number;
    completed: number;
  };
  recentLogs: Array<{
    time: string | null;
    botId: string;
    action: string;
    target: string;
    status: string;
    reasoning: string | null;
  }>;
  users: {
    total: number;
    activeToday: number;
    newThisWeek: number;
  };
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function StatCard({ label, value, sub, icon: Icon }: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
}) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-icon"><Icon size={18} /></div>
      <div className="admin-stat-body">
        <div className="admin-stat-value">{value}</div>
        <div className="admin-stat-label">{label}</div>
        {sub && <div className="admin-stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

function BarRow({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="admin-bar-row">
      <span className="admin-bar-label">{label}</span>
      <div className="admin-bar">
        <div className="admin-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="admin-bar-value">{value}</span>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminDashboardDataEurW();
      setData(result.data as DashboardData);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const maxAction = data
    ? Math.max(data.activity.today.post, data.activity.today.reply, data.activity.today.like, data.activity.today.message, 1)
    : 1;

  const maxHourly = data ? Math.max(...data.hourlyDistribution, 1) : 1;
  const currentHour = new Date().getUTCHours();

  return (
    <div className="admin-layout">
      <Navigation user={user} />
      <div className="admin-content">
        <div className="admin-topbar">
          <UserBar user={user} />
        </div>

        <div className="admin-dashboard">
          {/* Header */}
          <div className="admin-header">
            <h1>Admin Dashboard</h1>
            <div className="admin-header-actions">
              {lastRefresh && (
                <span className="admin-last-refresh">
                  Updated {lastRefresh.toLocaleTimeString()}
                </span>
              )}
              <button
                className="admin-refresh-btn"
                onClick={fetchData}
                disabled={loading}
              >
                <RefreshCw size={16} className={loading ? 'spin' : ''} />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="admin-error">
              <AlertTriangle size={16} /> {error}
            </div>
          )}

          {loading && !data ? (
            <div className="admin-loading">
              <Loader size={24} className="spin" />
              <span>Loading dashboard...</span>
            </div>
          ) : data && (
            <>
              {/* Stat Cards */}
              <div className="admin-stats-grid">
                <StatCard icon={Cpu} label="Active Bots" value={data.fleet.active} sub={`${data.fleet.paused} paused`} />
                <StatCard icon={Users} label="Users" value={data.users.total} sub={`${data.users.activeToday} active today`} />
                <StatCard icon={Activity} label="Actions Today" value={data.activity.today.total} sub={`vs ${data.activity.yesterday.total} yesterday`} />
                <StatCard icon={Zap} label="New This Week" value={data.users.newThisWeek} sub="new users" />
              </div>

              {/* Activity Breakdown */}
              <section className="admin-section">
                <h2><Activity size={16} /> Today's Activity</h2>
                <div className="admin-activity-grid">
                  <div>
                    <BarRow label="Posts" value={data.activity.today.post} max={maxAction} />
                    <BarRow label="Replies" value={data.activity.today.reply} max={maxAction} />
                    <BarRow label="Likes" value={data.activity.today.like} max={maxAction} />
                    <BarRow label="Messages" value={data.activity.today.message} max={maxAction} />
                  </div>
                  <div className="admin-comparison">
                    <h3>vs Yesterday</h3>
                    {(['post', 'reply', 'like', 'message'] as const).map((t) => {
                      const diff = data.activity.today[t] - data.activity.yesterday[t];
                      return (
                        <div key={t} className="admin-comparison-row">
                          <span>{t}</span>
                          <span className={diff > 0 ? 'positive' : diff < 0 ? 'negative' : ''}>
                            {diff > 0 ? `+${diff}` : diff === 0 ? '=' : diff}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>

              {/* Budget Utilization */}
              <section className="admin-section">
                <h2><BarChart2 size={16} /> Budget Utilization</h2>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Level</th>
                      <th>Bots</th>
                      <th>Avg Used</th>
                      <th>Budget</th>
                      <th>Utilization</th>
                      <th>Over</th>
                      <th>Idle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.budgetUtilization.map((row) => (
                      <tr key={row.level}>
                        <td>{row.level}</td>
                        <td>{row.bots}</td>
                        <td>{row.avgUsed}</td>
                        <td>{row.budget}</td>
                        <td>
                          <div className="admin-util-bar">
                            <div
                              className={`admin-util-fill ${row.utilPct > 100 ? 'over' : ''}`}
                              style={{ width: `${Math.min(row.utilPct, 100)}%` }}
                            />
                            <span>{row.utilPct}%</span>
                          </div>
                        </td>
                        <td className={row.over > 0 ? 'warn' : ''}>{row.over}</td>
                        <td>{row.idle}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              {/* Hourly Distribution */}
              <section className="admin-section">
                <h2><Clock size={16} /> Hourly Distribution (UTC)</h2>
                <div className="admin-hourly-chart">
                  {data.hourlyDistribution.map((count, h) => (
                    <div
                      key={h}
                      className={`admin-hourly-bar ${h === currentHour ? 'current' : ''}`}
                      title={`${String(h).padStart(2, '0')}:00 — ${count} actions`}
                    >
                      <div
                        className="admin-hourly-fill"
                        style={{ height: `${maxHourly > 0 ? (count / maxHourly) * 100 : 0}%` }}
                      />
                      <span className="admin-hourly-label">{h}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Engagement */}
              <section className="admin-section">
                <h2><Heart size={16} /> Engagement on Bot Content</h2>
                <div className="admin-stats-grid small">
                  <StatCard icon={Activity} label="Bot Posts" value={data.engagement.botPosts} />
                  <StatCard icon={Heart} label="Likes Received" value={data.engagement.totalLikes} />
                  <StatCard icon={Users} label="Human Replies" value={data.engagement.humanReplies} />
                </div>
                {data.engagement.topPosts.length > 0 && data.engagement.topPosts.some(p => p.likes > 0 || p.humanReplies > 0) && (
                  <div className="admin-top-posts">
                    <h3>Top Engaged Posts</h3>
                    {data.engagement.topPosts.filter(p => p.likes > 0 || p.humanReplies > 0).map((p) => (
                      <div key={p.id} className="admin-top-post">
                        <span className="admin-post-time">{timeAgo(p.timestamp)}</span>
                        <span className="admin-post-title">"{p.title}..."</span>
                        <span className="admin-post-stats">{p.likes}L {p.humanReplies}R</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Health */}
              {data.health.flaggedBots.length > 0 && (
                <section className="admin-section">
                  <h2><AlertTriangle size={16} /> Bot Health Flags</h2>
                  <table className="admin-table compact">
                    <thead>
                      <tr><th>Bot ID</th><th>Reports</th><th>Blocks</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {data.health.flaggedBots.map((b) => (
                        <tr key={b.id}>
                          <td className="mono">{b.id}</td>
                          <td className={b.reports > 5 ? 'warn' : ''}>{b.reports}</td>
                          <td className={b.blocks > 10 ? 'warn' : ''}>{b.blocks}</td>
                          <td>{b.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {/* Queue */}
              <section className="admin-section">
                <h2><Cpu size={16} /> Operation Queue</h2>
                <div className="admin-queue-grid">
                  <div className="admin-queue-item">
                    <span className="admin-queue-value">{data.queue.pending}</span>
                    <span className="admin-queue-label">Pending</span>
                  </div>
                  <div className="admin-queue-item">
                    <span className="admin-queue-value">{data.queue.processing}</span>
                    <span className="admin-queue-label">Processing</span>
                  </div>
                  <div className="admin-queue-item">
                    <span className="admin-queue-value warn">{data.queue.failed}</span>
                    <span className="admin-queue-label">Failed</span>
                  </div>
                  <div className="admin-queue-item">
                    <span className="admin-queue-value">{data.queue.completed}</span>
                    <span className="admin-queue-label">Completed</span>
                  </div>
                </div>
              </section>

              {/* Recent Logs */}
              <section className="admin-section">
                <h2><Clock size={16} /> Recent Activity</h2>
                <table className="admin-table compact">
                  <thead>
                    <tr><th>Time</th><th>Bot</th><th>Action</th><th>Target</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {data.recentLogs.map((log, i) => (
                      <tr key={i}>
                        <td className="nowrap">{timeAgo(log.time)}</td>
                        <td className="mono">{log.botId}</td>
                        <td>{log.action}</td>
                        <td className="mono">{log.target}</td>
                        <td className={log.status === 'FAILED' ? 'warn' : ''}>{log.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
